import { computed, ref, Ref, ComputedRef, onMounted, onUnmounted, onActivated } from 'vue'
import { getAsyncQueueObject } from '@/utils/async-queue'
import { clearToast, showToastLoading } from '@/utils/tools'
export interface PageParams {
  [x: string]: number
}
export interface PageListResut<T> {
  data: T[]
  total: number
  current_page: number
  last_page?: number // 后端返回最后一页是第几页
}
export interface cacheDataType<T, U> {
  [v: string]: {
    queryCache: combinePageParams<U> | null
    data: T[]
  }
}
export type combinePageParams<U> = PageParams & U

const { asyncQueueAdd } = getAsyncQueueObject()
export function useListScrollHandle(id: string): void {
  let listScrollTop = 0
  if (!id) {
    return
  }
  let el: HTMLElement
  function scrollHandle() {
    listScrollTop = el.scrollTop
  }
  onMounted(() => {
    el = document.getElementById(id)
    el.addEventListener('scroll', scrollHandle)
  })
  onUnmounted(() => {
    el.removeEventListener('scroll', scrollHandle)
  })
  onActivated(() => {
    if (!listScrollTop) {
      return
    }
    el.scrollTop = listScrollTop
  })
}

export default function useCommonList<T, U>(
  api: (params: U & PageParams) => Promise<PageListResut<T>>,
  options: {
    pageKey?: string
    pageSizeKey?: string
    pageKeyValue?: number
    pageSizeKeyValue?: number
    dataKey?: string
    getCacheKey?: () => string
    emptyText?: string
    defaultList?: Ref<T[]>
  }
): {
  refreshing: Ref<boolean>
  loading: Ref<boolean>
  requestError: Ref<boolean>
  finished: Ref<boolean>
  finishedText: ComputedRef<string>
  unshiftDataList: (data: T | T[]) => void
  queryHandle: (params?: U & PageParams) => Promise<T[]>
  onRefresh: () => any
  dataList: Ref<T[]>
  resetSearch: () => void
  setLoading: (value: boolean) => void
  getParam: () => U & PageParams
  showLoadingRefresh: () => Promise<void>
} {
  const refreshing = ref(false)
  const loading = ref(false)
  const requestError = ref(false)
  const finished = ref(false)
  const pageKey = options.pageKey || 'page'
  const pageSizeKey = options.pageSizeKey || 'per_page'
  const dataList: Ref<T[]> = ref(options.defaultList ? options.defaultList : [])

  let prevParams: U | null = null
  const queryBaseParams = {
    [pageKey]: options.pageKeyValue || 0,
    [pageSizeKey]: options.pageSizeKeyValue || 20,
  }
  const finishedText = computed(() => {
    return dataList.value.length ? '没有更多了' : options.emptyText || '暂无数据'
  })
  const queryHandle = async (params?: U, refresh?: boolean) => {
    if (!refresh && finished.value) {
      return
    }
    !refresh && (loading.value = true)
    queryBaseParams[pageKey]++
    try {
      prevParams = { ...params }
      const currentParams = {
        ...queryBaseParams,
        ...params,
      }
      const data: PageListResut<T> = await asyncQueueAdd(() => api(currentParams))
      loading.value = false

      dataList.value = refresh ? data.data : dataList.value.concat(data.data)
      if (
        dataList.value.length >= data.total ||
        (data?.last_page !== undefined && currentParams.page >= data?.last_page)
      ) {
        finished.value = true
      }
      refreshing.value = false
      return data.data
    } catch (error) {
      loading.value = false
      refreshing.value = false
      requestError.value = true
      console.log('error', error)
      return Promise.reject(error)
    }
  }

  const onRefresh = (setRefresh = true) => {
    finished.value = false
    refreshing.value = setRefresh
    queryBaseParams[pageKey] = 0
    return queryHandle({ ...prevParams }, true)
  }
  const showLoadingRefresh = async () => {
    showToastLoading()
    await onRefresh(false)
    clearToast()
  }
  const resetSearch = () => {
    finished.value = false
    queryBaseParams[pageKey] = 0
    dataList.value = []
    queryHandle()
  }
  const unshiftDataList = (data: T | T[]) => {
    if (Array.isArray(data)) {
      dataList.value = data.concat(dataList.value)
    } else {
      dataList.value.unshift(data)
    }
  }
  function setLoading(value: boolean) {
    loading.value = value
  }
  function getParam() {
    return {
      ...queryBaseParams,
      ...prevParams,
    }
  }
  return {
    getParam,
    setLoading,
    refreshing,
    loading,
    requestError,
    finished,
    queryHandle,
    onRefresh,
    dataList,
    unshiftDataList,
    resetSearch,
    finishedText,
    showLoadingRefresh,
  }
}
